import * as TabsPrimitive from "@radix-ui/react-tabs";
import { cva } from "class-variance-authority";
import * as React from "react";
import { useLocation } from "react-router-dom";

import { cn } from "../../../lib/utils";

import { Link } from "../../atoms/Link";
import { Text } from "../../atoms/Text";

/**
 * Individual tab configuration
 */
export type Tab = {
	/**
	 * Unique value for this tab
	 */
	value: string;
	/**
	 * Label to display in the tab trigger
	 */
	label: string | React.ReactNode;
	/**
	 * Content to display when this tab is active
	 */
	content: React.ReactNode;
	/**
	 * Whether this tab is disabled
	 */
	disabled?: boolean;
};

/**
 * Props for the Tabs component
 */
export type TabsProps = {
	/**
	 * Array of tab configurations
	 */
	tabs: Tab[];
	/**
	 * The currently active tab value (controlled)
	 */
	value: string;
	/**
	 * Callback fired when the active tab changes
	 */
	onValueChange?: (value: string) => void;
	/**
	 * The color variant of the tabs
	 * @default "neutral"
	 */
	color?: "neutral" | "primary";
	/**
	 * The size of the tabs
	 * @default "small"
	 */
	size?: "small" | "large";
	/**
	 * Whether to add spacing between tabs
	 * @default false
	 */
	spacing?: boolean;
	/**
	 * Optional URL parameter name to use for syncing tab state with the URL.
	 * When provided, clicking the tab will update the URL parameter.
	 * When not provided, the tab will work as a pure controlled component.
	 * @example urlParam="tab" will create URLs like ?tab=value
	 */
	urlParam?: string;
	/**
	 * Custom CSS class for the tabs container
	 */
	dangerouslySetClassName?: string;
	/**
	 * Optional test ID for the tabs container
	 */
	"data-testid"?: string;
};

const tabsVariants = cva("flex flex-col gap-2", {
	variants: {},
	defaultVariants: {},
});

const tabsListVariants = cva("inline-flex", {
	variants: {
		spacing: {
			true: "gap-2",
			false: "",
		},
	},
	defaultVariants: {
		spacing: false,
	},
});

const tabsTriggerVariants = cva(
	"flex cursor-pointer items-center justify-center gap-2 border border-solid text-center whitespace-nowrap transition-all focus-visible:ring-3 focus-visible:outline-1 disabled:pointer-events-none disabled:opacity-50",
	{
		variants: {
			color: {
				neutral:
					"border-neutral-3 bg-neutral-0 text-neutral-9 hover:bg-neutral-1 data-[state=active]:bg-neutral-2",
				primary:
					"border-primary-5 bg-neutral-0 text-primary-5 hover:bg-primary-1 data-[state=active]:bg-primary-5 data-[state=active]:text-neutral-0",
			},
			size: {
				small: "min-w-20 p-2",
				large: "w-[140px] min-w-[140px] p-2",
			},
			spacing: {
				true: "rounded-1",
				false: "first:rounded-l-1 last:rounded-r-1 [&:not(:first-child)]:-ml-px",
			},
		},
		defaultVariants: {
			color: "neutral",
			size: "small",
			spacing: false,
		},
	}
);

const tabsContentVariants = cva("flex-1 outline-hidden", {
	variants: {},
	defaultVariants: {},
});

/**
 * Tabs Component
 *
 * A tabbed interface component with optional URL parameter synchronization.
 * Built on top of Radix UI Tabs primitive with custom styling.
 *
 * @example
 * Basic usage (controlled component):
 * ```tsx
 * const [activeTab, setActiveTab] = useState("account");
 *
 * <Tabs
 *   tabs={[
 *     { value: "account", label: "Account", content: <div>Account content</div> },
 *     { value: "password", label: "Password", content: <div>Password content</div> },
 *   ]}
 *   value={activeTab}
 *   onValueChange={setActiveTab}
 * />
 * ```
 *
 * @example
 * With URL parameter synchronization and spacing:
 * ```tsx
 * const [activeTab, setActiveTab] = useState("account");
 *
 * <Tabs
 *   tabs={[
 *     { value: "account", label: "Account", content: <div>Account content</div> },
 *     { value: "password", label: "Password", content: <div>Password content</div> },
 *   ]}
 *   value={activeTab}
 *   onValueChange={setActiveTab}
 *   urlParam="tab"
 *   spacing
 *   color="primary"
 *   size="large"
 * />
 * ```
 */
export function Tabs({
	tabs,
	value,
	onValueChange = () => {},
	color = "neutral",
	size = "small",
	spacing = false,
	urlParam,
	dangerouslySetClassName,
	"data-testid": dataTestId,
}: TabsProps): React.ReactElement {
	const location = useLocation();

	const renderTrigger = (tab: Tab) => {
		const textSize = size === "large" ? "paragraph-3" : "caption";
		const content =
			typeof tab.label === "string" ? (
				<Text size={textSize} weight="semi-bold">
					{tab.label}
				</Text>
			) : (
				tab.label
			);

		// When using URL parameters, render as Link
		if (urlParam && !tab.disabled) {
			const buildHref = () => {
				const params = new URLSearchParams(location.search);
				params.set(urlParam, tab.value);
				return `${location.pathname}?${params.toString()}`;
			};

			return (
				<TabsPrimitive.Trigger key={tab.value} value={tab.value} asChild>
					<Link
						href={buildHref()}
						underline="never"
						dangerouslySetClassName={cn(tabsTriggerVariants({ color, size, spacing }))}
						data-testid={dataTestId ? `${dataTestId}-trigger-${tab.value}` : undefined}
					>
						{content}
					</Link>
				</TabsPrimitive.Trigger>
			);
		}

		// Standard button rendering (no URL parameters)
		return (
			<TabsPrimitive.Trigger
				key={tab.value}
				value={tab.value}
				disabled={tab.disabled}
				className={cn(tabsTriggerVariants({ color, size, spacing }))}
				data-testid={dataTestId ? `${dataTestId}-trigger-${tab.value}` : undefined}
			>
				{content}
			</TabsPrimitive.Trigger>
		);
	};

	return (
		<TabsPrimitive.Root
			data-slot="tabs"
			className={cn(tabsVariants(), dangerouslySetClassName)}
			value={value}
			onValueChange={onValueChange}
			data-testid={dataTestId}
		>
			<TabsPrimitive.List data-slot="tabs-list" className={cn(tabsListVariants({ spacing }))}>
				{tabs.map((tab) => renderTrigger(tab))}
			</TabsPrimitive.List>
			{tabs.map((tab) => (
				<TabsPrimitive.Content
					key={tab.value}
					data-slot="tabs-content"
					value={tab.value}
					className={cn(tabsContentVariants())}
				>
					{tab.content}
				</TabsPrimitive.Content>
			))}
		</TabsPrimitive.Root>
	);
}

Tabs.displayName = "Tabs";
